'use client'
import { alert } from '@repo/ui/Alert'
import { confirm } from '@repo/ui/Confirm'
import { ContactsOrganisationsList } from '@repo/contacts-organisations/ContactsOrganisationsList'
import { ContactsOrganisationsPreview } from '@repo/contacts-organisations/ContactsOrganisationsPreview'
import { CrossIcon } from '@repo/ui/CrossIcon'
import { EmptyState } from '@repo/ui/EmptyState'
import { LargeContactDetailsIcon } from '@repo/ui/LargeContactDetailsIcon'
import { OrganisationsAddEditForm } from '@repo/contacts-organisations/OrganisationsAddEditForm'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useState, useEffect } from 'react'
import { useWindowSize } from '@uidotdev/usehooks'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import PropTypes from 'prop-types'

export const OrganisationsManager = ({
  preSelectExpertTypeId,
  onSelect = () => {},
  initialSelectedItem,
  sx = {},
  onEditModeChange = () => {},
  onCreateModeChange = () => {},
  testData
}) => {
  const mode = 'organisations'
  const { organisationsById, refreshOrganisationsTrigger, deleteOrganisation } = useLookupsStore()
  const { allowedActions } = useMenuItemsStore()
  const allowEditOrganisations = allowedActions?.editOrganisations?.allowed || false
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const [selectedItem, setSelectedItem] = useState(initialSelectedItem || null)
  const [organisation, setOrganisation] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const { height: windowHeight } = useWindowSize()

  useEffect(() => {
    const id = selectedItem?.organisationId || null
    if (id && organisationsById?.[id]) {
      setOrganisation(organisationsById[id])
      setShowCreateForm(false)
      onSelect(organisationsById[id])
    }
  }, [selectedItem, organisationsById, onSelect])

  useEffect(() => {
    const id = selectedItem?.organisationId || null
    if (id && !organisationsById?.[id]) {
      setSelectedItem(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshOrganisationsTrigger])

  useEffect(() => {
    onCreateModeChange(showCreateForm)
  }, [showCreateForm, onCreateModeChange])

  const displayCreateForm = () => {
    setSelectedItem(null)
    setOrganisation(null)
    setShowCreateForm(true)
  }

  const handleNewOrganisation = ({ organisationId }) => {
    setSelectedItem({ organisationId })
    setShowCreateForm(false)
  }

  const handleDelete = async (item, mode) => {
    const proceed = await confirm('Are you sure you want to delete this item?')
    if (!proceed) return

    if (mode === 'organisations') {
      try {
        await deleteOrganisation({ id: item.organisationId })

        setSelectedItem(null)
        setOrganisation(null)
        onSelect(null)
      } catch (error) {
        console.error(error)
        alert('We could not delete this organisation. It may be linked to a contact.')
      }
    }
  }

  const marginTop = pageTitleBlockIsVisible ? 0 : '140px'

  return (
    <MUIStack
      data-testid="OrganisationsManager"
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      gap={'30px'}
      sx={{
        width: 'calc(100% - 80px)',
        maxWidth: '1200px',
        margin: '0 auto',
        marginTop,
        padding: '14px 30px',
        height: `calc(${windowHeight}px - 180px)`,
        minHeight: '500px',
        backgroundColor: 'white',
        borderRadius: '6px',
        border: '1px solid #D8DCE5',
        position: 'relative',
        ...sx
      }}
    >
      <ContactsOrganisationsList
        onSelect={setSelectedItem}
        selectedItem={selectedItem}
        mode={mode}
        preSelectExpertTypeId={preSelectExpertTypeId}
        testData={testData}
      />

      {allowEditOrganisations ? (
        <MUIStack
          direction="row"
          justifyContent="flex-end"
          data-testid="newButton"
          sx={{
            flex: 1,
            padding: '4px 0px',
            zIndex: 1,
            position: 'absolute',
            top: '16px',
            right: '30px'
          }}
        >
          <MUIButton variant="contained" color="primary" onClick={displayCreateForm} sx={{ width: '220px' }}>
            <CrossIcon sx={{ width: '18px', height: '18px', mr: 2, color: 'white', transform: 'rotate(45deg)' }} />
            New organisation
          </MUIButton>
        </MUIStack>
      ) : null}

      <MUIStack
        direction="column"
        sx={{
          flex: 1,
          marginTop: '59px',
          overflowY: 'auto',
          backgroundColor: 'white',
          padding: '20px 0px',
          borderRadius: '6px',
          height: 'calc(100% - 64px)'
        }}
      >
        {!selectedItem && showCreateForm ? (
          <div style={{ padding: '0px 20px' }}>
            <OrganisationsAddEditForm onCancel={() => setShowCreateForm(false)} onSuccess={handleNewOrganisation} />
          </div>
        ) : null}

        {!selectedItem && !showCreateForm ? (
          <EmptyState
            text="Select an item to see details"
            icon={<LargeContactDetailsIcon sx={{ mb: 8, width: '64px', height: '64px' }} />}
          />
        ) : null}

        {organisation?.organisationId ? (
          <ContactsOrganisationsPreview
            itemId={organisation.organisationId}
            mode="organisations"
            onEditModeChange={onEditModeChange}
            handleDelete={handleDelete}
          />
        ) : null}
      </MUIStack>
    </MUIStack>
  )
}

OrganisationsManager.propTypes = {
  preSelectExpertTypeId: PropTypes.any,
  onSelect: PropTypes.func,
  initialSelectedItem: PropTypes.any,
  sx: PropTypes.object,
  onEditModeChange: PropTypes.func,
  onCreateModeChange: PropTypes.func,
  testData: PropTypes.any
}
